import { z } from 'zod';
import { CreditResolutionMethod, CrewClaimResolutionMethodEnum, } from '../../enums/crew.js';
import { claimCustomerPaymentStatus } from './claimCustomerPaymentStatus.js';
import { financialSectionItem } from './financialSection.js';
// a calculated order line has a string id before the claim exists, a claim line item a number after
const lineId = z.union([z.string(), z.number()]);
const amount = z.number().nonnegative();
const creditMethod = z.enum(CreditResolutionMethod);
// ---------- input: the claim as it stands, independent of where it came from ----------
const exchangeItem = z.object({
    idFromPlatform: z.string(),
    price: amount,
    quantity: z.number().int().positive(),
});
const claimCalculationLineItem = z.object({
    id: lineId,
    quantity: z.number().int().positive(),
    // per unit; the adapter owns dividing a line-total tax down
    unitPrice: amount,
    unitTax: amount,
    requestedResolutionMethod: z.enum(CrewClaimResolutionMethodEnum),
    resolutionMethod: z.enum(CrewClaimResolutionMethodEnum).nullable(),
    state: z.enum(['open', 'denied', 'canceled', 'finalized']),
    // the merchant's issued amount when it differs from the returned value
    creditAmountOverride: amount.nullable(),
    // what the customer was shown as owed at submission; reported as requested over the recomputed value
    storedRequestedAmount: amount.nullable(),
    // deductions already taken from this line by an earlier finalization
    previouslyDeducted: amount,
    outstandingCreditFinalized: z.boolean(),
    exchange: z
        .object({
        items: z.array(exchangeItem),
        // whether exchanged items are worth their own price or the returned line's (a like-for-like replacement)
        priceBasis: z.enum(['exchangedItems', 'returnedLine']),
        isShopNow: z.boolean(),
    })
        .nullable(),
    // how a downsell is paid back when the line names it
    downsellMethod: creditMethod.nullable(),
    // what the customer was charged or promised for the exchange; the money follows it over the recomputed delta
    storedExchangeDelta: z.number().nullable(),
    fees: z.array(z.object({ amount, label: z.string() })),
    resolvedExternally: z.boolean(),
});
export const claimCalculationInput = z.object({
    handlingFee: z.object({ amount, label: z.string() }).nullable(),
    returnShipping: amount,
    exchangeShipping: amount,
    giftCardIncentive: amount,
    shopNowIncentive: amount,
    // how credit goes back to the customer when a line does not say
    aggregateCreditResolutionMethod: creditMethod,
    customerPayment: z
        .object({
        status: claimCustomerPaymentStatus,
        amount,
        refundedAmount: amount,
    })
        .nullable(),
    exchangeMethod: z.enum(['instant', 'standard']),
    lineItems: z.array(claimCalculationLineItem),
});
// ---------- output ----------
const pricedLine = z.object({
    id: lineId,
    // set on exchanged items, so a deduction can be shown as the new item rather than the returned one
    idFromPlatform: z.string().optional(),
    quantity: z.number(),
    unitPrice: z.number(),
    unitTax: z.number(),
});
const financialSection = z.object({
    lineItems: z.array(pricedLine),
    sections: z.array(financialSectionItem),
    total: z.number(),
});
// issued money is read back from the line as finalization left it; pending money is recomputed
const outcomeStatus = z.enum(['pending', 'issued']);
const creditOutcome = z.object({
    kind: z.literal('credit'),
    id: lineId,
    status: outcomeStatus,
    method: z.enum([
        CrewClaimResolutionMethodEnum.refund,
        CrewClaimResolutionMethodEnum.giftCard,
        CrewClaimResolutionMethodEnum.other,
    ]),
    requestedAmount: z.number(),
    deductedAmount: z.number(),
    amount: z.number(),
    wasZeroedThroughDeduction: z.boolean(),
    // a downsell paid back to the customer, alongside the exchange outcome for the same line
    isPartialExchangeCredit: z.boolean(),
    resolvedExternally: z.boolean(),
});
const exchangeOutcome = z.object({
    kind: z.literal('exchange'),
    id: lineId,
    status: outcomeStatus,
    method: z.enum([
        CrewClaimResolutionMethodEnum.variantExchange,
        CrewClaimResolutionMethodEnum.replacementOrder,
    ]),
    returnedValue: z.number(),
    exchangedValue: z.number(),
    // positive is an upsell the customer owes, negative a downsell they are owed
    exchangeDelta: z.number(),
    // the persisted delta finalization acts on; differs from exchangeDelta when the stored figure is stale
    settledDelta: z.number().nullable(),
    downsellMethod: creditMethod.nullable(),
    isShopNow: z.boolean(),
    resolvedExternally: z.boolean(),
});
const noMoneyOutcome = z.object({
    kind: z.literal('none'),
    id: lineId,
    method: z.enum([
        CrewClaimResolutionMethodEnum.repair,
        CrewClaimResolutionMethodEnum.other,
    ]),
});
const excludedOutcome = z.object({
    kind: z.literal('excluded'),
    id: lineId,
    reason: z.enum([
        'denied',
        'canceled',
        'warrantyReview',
        'missingExchangeItems',
    ]),
});
export const claimCalculationLineOutcome = z.discriminatedUnion('kind', [
    creditOutcome,
    exchangeOutcome,
    noMoneyOutcome,
    excludedOutcome,
]);
export const claimCalculationResult = z.object({
    credits: financialSection,
    deductions: financialSection,
    // deductions minus credits: positive the customer owes, negative the customer is owed
    balance: z.number(),
    payment: z.object({
        intentAmount: z.number(),
        waivedAmount: z.number(),
    }),
    lines: z.array(claimCalculationLineOutcome),
    // deductions the credit lines could not absorb
    unallocatedDeduction: z.number(),
    // what the customer gets across the whole claim, split by whether it has been issued yet
    totals: z.object({
        refund: z.number(),
        giftCard: z.number(),
        issued: z.object({ refund: z.number(), giftCard: z.number() }),
        pending: z.object({ refund: z.number(), giftCard: z.number() }),
    }),
    benefit: z.object({
        newRevenue: z.number(),
        retainedRevenue: z.number(),
    }),
});
// what is stored on the claim: the result plus everything needed to reproduce it
export const claimCalculation = claimCalculationResult.extend({
    calculatedOn: z.string(),
    input: claimCalculationInput,
});
//# sourceMappingURL=claimCalculation.js.map